import { FunctionComponent, useCallback } from 'react'
import { ActionList, Avatar, Text, getTheme } from '@conexasaude/conexa-hero'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useHistory } from 'react-router'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { Container } from '@/components/Container'
import { RenderIf } from '../RenderIf'
import { useNavbar } from './useNavbar'
import useStyles from '@/components/Navbar/styles'
import logo from '@/assets/logo-conexa.svg'
import Icon from '@/components/RebrandedHeroIcons'
import useBreakpoints from '@/utils/useBreakpoints'

const Navbar: FunctionComponent = () => {
  const { embed } = useThemeGeneratorContext()
  const { user, clinic } = useAuthContext()
  const { pathname } = useLocation()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { isPhone } = useBreakpoints()
  const { states } = useNavbar()
  const theme = getTheme()
  const history = useHistory()
  const styles = useStyles({
    embed: embed?.value,
    conexaLogo: !clinic?.logos?.show,
  })

  const handleAnalytics = useCallback(
    (eventName: string) => {
      if (!eventName) return
      logAnalyticsEvent(eventName)
    },
    [logAnalyticsEvent],
  )

  const handleNavigation = useCallback(
    (
      path: string,
      analyticsEventName: string,
      itemType: string,
      callback?: (...args: unknown[]) => void,
    ) => {
      const hasButtonType = (itemType: string) => /button/.test(itemType)
      handleAnalytics(analyticsEventName)

      if (hasButtonType(itemType) && callback) {
        return callback()
      }

      history.push(path)
    },
    [history, handleAnalytics],
  )

  const isActiveNavItem = useCallback(
    (itemPath: string) => {
      const homePath = '/'

      if (pathname === homePath && itemPath === pathname) return true
      else if (itemPath !== homePath) return pathname.includes(itemPath)
      return false
    },
    [pathname],
  )

  const handleProfileNavigation = useCallback(
    (item: typeof states.profileAvatarItems[0]) => {
      handleAnalytics(item.analyticsEvent)
      item.onClick()
    },
    [handleAnalytics, states],
  )

  return (
    <div css={styles.headerContainer}>
      <RenderIf condition={!embed.value}>
        <Container>
          <nav css={styles.navContainer}>
            <img
              css={styles.navLogo}
              src={clinic?.logos.show ? clinic.logos.menu : logo}
              alt="Logo"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = logo
              }}
            />

            <ActionList>
              <ActionList.Target>
                <div css={styles.navProfile}>
                  <Avatar
                    label={isPhone ? null : user?.shortName}
                    size="medium"
                    src={user?.photo}
                  />
                  <Icon
                    name="arrow-down"
                    color={theme.colors.neutral[200]}
                    width={theme.spacing.sm}
                  />
                </div>
              </ActionList.Target>

              <ActionList.Dropdown>
                {states.profileAvatarItems?.map(item => (
                  <RenderIf condition={item?.show} key={item.id}>
                    <ActionList.Item
                      cy={item?.cy}
                      onClick={() => handleProfileNavigation(item)}
                    >
                      {item.label}
                    </ActionList.Item>
                  </RenderIf>
                ))}
              </ActionList.Dropdown>
            </ActionList>
          </nav>
        </Container>

        <nav css={styles.navContainerBreadcrumb}>
          <Container>
            <ul css={styles.navListItems}>
              {states.menuItems?.map(item => (
                <RenderIf condition={item?.show} key={item.id}>
                  <li
                    css={[styles.navItem, item?.css]}
                    onClick={() =>
                      handleNavigation(
                        item?.path,
                        item?.analyticsEvent,
                        item?.type,
                        item?.onClick,
                      )
                    }
                    data-cy={item?.cy}
                  >
                    <Icon
                      name={item.icon}
                      color={theme.colors.background.white}
                    />
                    <Text
                      color={theme.colors.background.white}
                      css={
                        isActiveNavItem(item.path)
                          ? styles.navItemActiveText
                          : styles.navItemText
                      }
                      cy={item?.cy}
                    >
                      {item.label}
                    </Text>
                  </li>
                </RenderIf>
              ))}
            </ul>
          </Container>
        </nav>
      </RenderIf>
    </div>
  )
}

export default Navbar
